import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  List,
  ListOrdered,
  Link2,
  ImageIcon,
  X,
  Upload,
  Smile,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useEffect, useState } from "react";
import MediaUploadDialog from "./media-upload-dialog";

interface SuggestionEditorProps {
  initialContent?: any;
  onUpdate: (data: { content: any; plainContent: string }) => void;
  placeholder?: string;
  screenshots?: Array<{ name: string; data: string }>;
  onScreenshotsChange?: (screenshots: Array<{ name: string; data: string }>) => void;
}

const EMOJI_LIST = [
  "😀", "😃", "😄", "😁", "😅", "😂", "🤣", "😊", "😇", "🙂",
  "🙃", "😉", "😌", "😍", "🥰", "😘", "😗", "😙", "😚", "😋",
  "😛", "😝", "😜", "🤪", "🤨", "🧐", "🤓", "😎", "🤩", "🥳",
  "😏", "😒", "😞", "😔", "😟", "😕", "🙁", "😣", "😖", "😫",
  "😩", "🥺", "😢", "😭", "😤", "😠", "😡", "🤬", "🤯", "😳",
  "🥵", "🥶", "😱", "😨", "😰", "😥", "😓", "🤗", "🤔", "🤭",
  "🤫", "🤥", "😶", "😐", "😑", "😬", "🙄", "😯", "😦", "😧",
  "👍", "👎", "👏", "🙌", "👌", "✌️", "🤞", "🤝", "🙏", "💪",
  "❤️", "🧡", "💛", "💚", "💙", "💜", "🖤", "🤍", "💔", "❣️",
  "💕", "💞", "💓", "💗", "💖", "💘", "💝", "⭐", "🌟", "✨",
  "💯", "🔥", "💥", "⚡", "💫", "💬", "💭", "🗨️", "👀", "🎉",
];

export default function SuggestionEditor({
  initialContent,
  onUpdate,
  placeholder = "Describe your suggestion or report an error...",
  screenshots = [],
  onScreenshotsChange,
}: SuggestionEditorProps) {
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showMediaDialog, setShowMediaDialog] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-primary underline',
        },
      }),
      Image.configure({
        HTMLAttributes: {
          class: 'max-w-full h-auto rounded-lg',
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
    ],
    content: initialContent || { type: 'doc', content: [{ type: 'paragraph' }] },
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none min-h-[200px] p-4',
      },
    },
    onUpdate: ({ editor }) => {
      const json = editor.getJSON();
      const text = editor.getText();
      onUpdate({
        content: json,
        plainContent: text,
      });
    },
  });

  useEffect(() => {
    return () => {
      editor?.destroy();
    };
  }, [editor]);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const fileArray = Array.from(files).filter(file => file.type.startsWith('image/'));
    let loadedCount = 0;
    const newScreenshots: Array<{ name: string; data: string }> = [];

    fileArray.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64 = event.target?.result as string;
        newScreenshots.push({ name: file.name, data: base64 });
        loadedCount++;

        if (loadedCount === fileArray.length) {
          onScreenshotsChange?.([...screenshots, ...newScreenshots]);
        }
      };
      reader.readAsDataURL(file);
    });

    e.target.value = '';
  };

  const removeScreenshot = (index: number) => {
    const newScreenshots = screenshots.filter((_, i) => i !== index);
    onScreenshotsChange?.(newScreenshots);
  };

  const addLink = () => {
    if (linkUrl && editor) {
      editor.chain().focus().setLink({ href: linkUrl }).run();
      setLinkUrl("");
      setShowLinkInput(false);
    }
  };

  const insertEmoji = (emoji: string) => {
    if (editor) {
      editor.chain().focus().insertContent(emoji).run();
      setShowEmojiPicker(false);
    }
  };

  const handleMediaSelected = (url: string) => {
    if (editor) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  };

  if (!editor) {
    return null;
  }

  return (
    <div className="border rounded-lg overflow-hidden">
      <div className="bg-muted/50 border-b p-2 flex flex-wrap items-center gap-1">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={editor.isActive('bold') ? 'bg-muted' : ''}
          data-testid="button-bold"
        >
          <Bold className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={editor.isActive('italic') ? 'bg-muted' : ''}
          data-testid="button-italic"
        >
          <Italic className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className={editor.isActive('underline') ? 'bg-muted' : ''}
          data-testid="button-underline"
        >
          <UnderlineIcon className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleStrike().run()}
          className={editor.isActive('strike') ? 'bg-muted' : ''}
          data-testid="button-strikethrough"
        >
          <Strikethrough className="h-4 w-4" />
        </Button>

        <Separator orientation="vertical" className="h-6 mx-1" />

        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={editor.isActive('bulletList') ? 'bg-muted' : ''}
          data-testid="button-bullet-list"
        >
          <List className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={editor.isActive('orderedList') ? 'bg-muted' : ''}
          data-testid="button-ordered-list"
        >
          <ListOrdered className="h-4 w-4" />
        </Button>

        <Separator orientation="vertical" className="h-6 mx-1" />

        <Popover open={showLinkInput} onOpenChange={setShowLinkInput}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className={editor.isActive('link') ? 'bg-muted' : ''}
              data-testid="button-link"
            >
              <Link2 className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80">
            <div className="flex gap-2">
              <Input
                placeholder="Enter URL"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addLink();
                  }
                }}
                data-testid="input-link-url"
              />
              <Button type="button" onClick={addLink} size="sm" data-testid="button-add-link">
                Add
              </Button>
            </div>
          </PopoverContent>
        </Popover>

        <Popover open={showEmojiPicker} onOpenChange={setShowEmojiPicker}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              data-testid="button-emoji"
            >
              <Smile className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80">
            <div className="grid grid-cols-10 gap-1 max-h-60 overflow-y-auto">
              {EMOJI_LIST.map((emoji, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => insertEmoji(emoji)}
                  className="text-2xl hover:bg-muted rounded p-1 transition-colors"
                  data-testid={`emoji-${index}`}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>

        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setShowMediaDialog(true)}
          className="bg-red-500 hover:bg-red-600 text-white rounded-full min-h-[44px] min-w-[44px]"
          data-testid="button-add-media"
        >
          <ImageIcon className="h-5 w-5" />
        </Button>

        <label htmlFor="screenshot-upload">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            asChild
            data-testid="button-upload-screenshot"
          >
            <span>
              <Upload className="h-4 w-4" />
            </span>
          </Button>
          <input
            id="screenshot-upload"
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={handleFileUpload}
          />
        </label>
      </div>

      <EditorContent editor={editor} className="bg-background" />

      {screenshots.length > 0 && (
        <div className="border-t p-3 bg-muted/30">
          <div className="text-sm font-medium mb-2">Screenshots ({screenshots.length})</div>
          <div className="grid grid-cols-2 gap-2">
            {screenshots.map((screenshot, index) => (
              <div
                key={index}
                className="relative group rounded-lg overflow-hidden border"
                data-testid={`screenshot-${index}`}
              >
                <img
                  src={screenshot.data}
                  alt={screenshot.name}
                  className="w-full h-24 object-cover"
                />
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  className="absolute top-1 right-1 h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => removeScreenshot(index)}
                  data-testid={`button-remove-screenshot-${index}`}
                >
                  <X className="h-3 w-3" />
                </Button>
                <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs p-1 truncate">
                  {screenshot.name}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <MediaUploadDialog
        open={showMediaDialog}
        onOpenChange={setShowMediaDialog}
        onMediaSelected={handleMediaSelected}
      />
    </div>
  );
}
